import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import Cookies from 'js-cookie';
import { AUTH_API, LOCAL_API } from './config/api';

// Create a broadcast channel for logout synchronization
const logoutChannel = new BroadcastChannel("logout");

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem("user");
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [inactivityTimer, setInactivityTimer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [globalRole, setGlobalRole] = useState(null);
  const [permissions, setPermissions] = useState([]);
  // Add this to prevent multiple auth checks
  const authCheckComplete = useRef(false);

  // Function to check if we have auth cookies and process them
  const checkAndHandleAuthCookies = useCallback(async () => {
    // Only proceed if we haven't completed an auth check already
    if (authCheckComplete.current) {
      return false;
    }

    try {
      // Get the tokens only
      const accessToken = Cookies.get('access_token');
      const refreshToken = Cookies.get('refresh_token');
  
      if (accessToken && refreshToken) {
        // Mark that we've started an auth check
        authCheckComplete.current = true;

        // Skip the problematic cookie parsing entirely and go straight to API
        console.log("Found auth tokens, fetching user data from API...");
        const response = await fetch(LOCAL_API.user, {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        });
      
        if (response.ok) {
          const userData = await response.json();
          
          console.log("Got user data from API:", userData);
          
          const userWithTokens = {
            ...userData,
            access_token: accessToken,
            refresh_token: refreshToken
          };
          
          setUser(userWithTokens);
          
          // Store in localStorage to preserve across refreshes
          localStorage.setItem("user", JSON.stringify(userWithTokens));
          
          return true;
        } else {
          console.error("Error fetching user data:", response.status);
          // Mark authentication as complete even if it failed
          authCheckComplete.current = true;
        }
      }
      // Mark authentication as complete
      authCheckComplete.current = true;
      return false;
    } catch (error) {
      console.error("Error in auth check:", error);
      // Mark authentication as complete even if it failed
      authCheckComplete.current = true;
      return false;
    }
  }, []);
  

  const logout = useCallback(async () => {
    if (!user) return;

    try {
      const response = await fetch(AUTH_API.logout, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user.token || user.access_token}`,
        },
        body: JSON.stringify({
          refresh_token: user.refresh_token,
        }),
      });

      if (response.ok) {
        console.log('Logout successful');
      } else {
        console.warn('Logout request failed, but proceeding with local logout');
      }
      
      // Always clean up local state
      setUser(null);
      localStorage.removeItem("user");
      clearTimeout(inactivityTimer);
      
      // Clear cookies
      Cookies.remove('access_token');
      Cookies.remove('refresh_token');
      
      // Reset the auth check flag so we can authenticate again
      authCheckComplete.current = false;
      
      // Notify other tabs
      logoutChannel.postMessage({ type: "LOGOUT" });
      localStorage.setItem("logout", Date.now().toString());
      setGlobalRole(null);
      setPermissions([]);
      
    } catch (error) {
      console.error("Error during logout:", error);
      // Clean up even if the server request fails
      setUser(null);
      localStorage.removeItem("user");
      clearTimeout(inactivityTimer);
      // Clear cookies
      Cookies.remove('access_token');
      Cookies.remove('refresh_token');
      // Reset the auth check flag
      authCheckComplete.current = false;
    }
  }, [user, inactivityTimer]);

  const refreshToken = useCallback(async () => {
    if (!user?.refresh_token) return null;
  
    try {
      const response = await fetch(LOCAL_API.tokenRefresh, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          refresh: user.refresh_token,
        }),
      });
  
      const data = await response.json();
  
      if (response.ok) {
        const updatedUser = { 
          ...user, 
          token: data.access,
          access_token: data.access // Store in both formats for compatibility
        };
        setUser(updatedUser);
        localStorage.setItem("user", JSON.stringify(updatedUser));
        
        // Update the cookie as well
        Cookies.set('access_token', data.access, { 
          expires: 1/24, // 1 hour
          sameSite: 'Lax',
          secure: window.location.protocol === 'https:'
        });
        
        console.log('Token refreshed successfully');
        return data.access;
      } else {
        console.error("Token refresh failed:", data);
        await logout();
        return null;
      }
    } catch (error) {
      console.error("Error refreshing token:", error);
      await logout();
      return null;
    }
  }, [user, logout]);

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
    }

    // 50 minutes inactivity timeout
    const timer = setTimeout(() => {
      console.log('Inactivity timeout reached, logging out');
      logout();
    }, 3000000); // 50 minutes

    setInactivityTimer(timer);
  }, [inactivityTimer, logout]);

  const login = useCallback(
    async (userData) => {
      try {
        setLoading(true);
        
        // Format the user data consistently
        const userWithTokens = {
          ...userData,
          token: userData.access_token, // Keep both formats for compatibility
          access_token: userData.access_token,
          refresh_token: userData.refresh_token,
        };
        
        // Update state
        setUser(userWithTokens);
        localStorage.setItem("user", JSON.stringify(userWithTokens));
        
        // Set cookies for persistent auth
        Cookies.set('access_token', userData.access_token, { 
          expires: 1/24, // 1 hour
          sameSite: 'Lax',
          secure: window.location.protocol === 'https:'
        });
        
        Cookies.set('refresh_token', userData.refresh_token, { 
          expires: 7, // 7 days
          sameSite: 'Lax',
          secure: window.location.protocol === 'https:'
        });
        
        // Reset auth check flag - we now have a valid user
        authCheckComplete.current = true;
        
        // Start inactivity timer
        resetInactivityTimer();
        
        console.log('Login successful', userWithTokens.email || userWithTokens.username);
        return userWithTokens;
      } catch (error) {
        console.error("Login error:", error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [resetInactivityTimer]
  );

  const initiateGoogleLogin = useCallback(async (isCompanyWizard = false, email = '') => {
    try {
      setLoading(true);
      console.log('Initiating Google login process');
      
      // Build the query parameters
      let url = `${AUTH_API.googleInit}/`;
      const params = new URLSearchParams();
      
      if (isCompanyWizard) {
        params.append('redirectType', 'popup');
      }
      
      if (email) {
        params.append('email', email);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      // Make the request
      const response = await fetch(url);
      const data = await response.json();
      
      // Handle the response
      if (response.ok && data.authorization_url) {
        console.log('Redirecting to Google authorization URL:', data.authorization_url);
        
        // Validate URL for security
        if (!data.authorization_url.startsWith('https://')) {
          throw new Error('Invalid authorization URL');
        }
        
        // Open the URL in appropriate mode
        if (isCompanyWizard) {
          window.open(data.authorization_url, '_blank', 'width=600,height=700');
        } else {
          window.location.href = data.authorization_url;
        }
        return true;
      } else {
        throw new Error(data.error || 'Failed to initiate Google login');
      }
    } catch (error) {
      console.error('Google login initiation error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle broadcast channel for logout synchronization across tabs
  useEffect(() => {
    const handleLogoutMessage = (event) => {
      if (event.data.type === "LOGOUT") {
        setUser(null);
        localStorage.removeItem("user");
        clearTimeout(inactivityTimer);
        // Clear cookies
        Cookies.remove('access_token');
        Cookies.remove('refresh_token');
        // Reset auth check flag
        authCheckComplete.current = false;
      }
    };

    logoutChannel.addEventListener("message", handleLogoutMessage);
    return () => logoutChannel.removeEventListener("message", handleLogoutMessage);
  }, [inactivityTimer]);

  // Handle storage events for logout synchronization
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "logout") {
        setUser(null);
        localStorage.removeItem("user");
        clearTimeout(inactivityTimer);
        // Clear cookies
        Cookies.remove('access_token');
        Cookies.remove('refresh_token');
        // Reset auth check flag
        authCheckComplete.current = false;
      } else if (event.key === "user" && event.newValue === null) {
        setUser(null);
        clearTimeout(inactivityTimer);
        // Reset auth check flag
        authCheckComplete.current = false;
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [inactivityTimer]);

  // Reset inactivity timer on user activity
  useEffect(() => {
    if (!user) return;

    const events = ["mousedown", "mousemove", "keypress", "scroll", "touchstart"];
    const resetTimer = () => resetInactivityTimer();

    events.forEach((event) => window.addEventListener(event, resetTimer));
    return () => events.forEach((event) => window.removeEventListener(event, resetTimer));
  }, [user, resetInactivityTimer]);

  // Clean up cookies and perform auth check only once on mount
  useEffect(() => {
    // Clean up the problematic cookie to prevent future issues
    if (Cookies.get('user_data')) {
      console.log("Removing problematic user_data cookie");
      Cookies.remove('user_data');
    }
    
    // If we already have a user from localStorage, mark auth as complete
    if (user) {
      authCheckComplete.current = true;
      return;
    }

    // Only run the auth check if we haven't done it yet and don't have a user
    if (!authCheckComplete.current) {
      (async () => {
        await checkAndHandleAuthCookies();
      })();
    }

    // Check URL for error parameters
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    
    if (error) {
      console.error('Authentication error from URL:', error);
      // Clean up URL params
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [checkAndHandleAuthCookies, user]);

  // Fetch global role and permissions when user changes
  useEffect(() => {
    if (!user) {
      setGlobalRole(null);
      setPermissions([]);
      return;
    }
    const token = user.access_token || user.token;
    if (!token) return;

    (async () => {
      try {
        const res = await fetch(AUTH_API.globalRole, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (res.ok && data.success) {
          setGlobalRole(data.data?.globalRole || null);
          setPermissions(data.data?.permissions || []);
        }
      } catch (err) {
        console.error("Failed to fetch global role:", err);
      }
    })();
  }, [user]);

  // Provide auth context values to children
  const value = {
    user,
    login,
    logout,
    loading,
    resetInactivityTimer,
    refreshToken,
    initiateGoogleLogin,
    checkAndHandleAuthCookies,
    isAuthenticated: !!user,
    globalRole,
    permissions,
    isAdmin: globalRole === "admin" || globalRole === "super_admin",
    isSuperAdmin: globalRole === "super_admin",
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
